import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ReferenceLine, AreaChart, Area } from "recharts";
import { Users, TrendingDown, Clock, Percent, HeartHandshake, DollarSign, Pencil, CalendarDays, ExternalLink, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import ReminderSettings from "@/components/ReminderSettings";
import DigestPanel from "@/components/DigestPanel";

const NAVY = "#0A2A5E", BLUE = "#1E5AB8", ORANGE = "#F5A623", ROSE = "#E11D48", GREEN = "#059669";
const fmtMonth = (m) => new Date(`${m}-15`).toLocaleString("en-US", { month: "short", year: "2-digit" });

function KpiCard({ icon: Icon, label, value, unit, delta, good, tid }) {
  const up = delta > 0;
  const positive = good === "up" ? up : !up;
  return (
    <Card data-testid={tid} className="p-4 bg-white border-slate-200">
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-lg bg-[#FDECD0] flex items-center justify-center"><Icon className="w-4 h-4 text-[#7C3F00]" /></div>
        {delta != null && Number.isFinite(delta) && (
          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${positive ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{up ? "▲" : "▼"} {Math.abs(delta)}{unit === "%" ? " pts" : unit === "$" ? "" : ""}</span>
        )}
      </div>
      <div className="font-display text-xl sm:text-2xl font-extrabold text-[#0A2A5E] mt-3 break-words">{unit === "$" && "$"}{value}{unit && unit !== "$" && <span className="text-sm text-slate-500 ml-1">{unit}</span>}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </Card>
  );
}

function ChartCard({ title, subtitle, children, tid }) {
  return (
    <Card data-testid={tid} className="p-5 bg-white border-slate-200">
      <div className="mb-3"><h3 className="font-display font-bold text-[#0A2A5E]">{title}</h3><p className="text-xs text-slate-500">{subtitle}</p></div>
      <div className="h-64 min-w-0" style={{ minHeight: 256 }}>{children}</div>
    </Card>
  );
}

const CHART_MARGIN = { top: 5, right: 10, left: -10, bottom: 0 };
const AXIS_TICK = { fontSize: 11 };
const LEGEND_STYLE = { fontSize: 11 };

function KpiEditor({ open, onOpenChange, fields, rows, onSaved }) {
  const [month, setMonth] = useState("");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const empty = useMemo(() => Object.fromEntries(fields.map((f) => [f.key, ""])), [fields]);

  useEffect(() => {
    if (!open) return;
    const last = rows[rows.length - 1];
    const m = last ? last.month : "";
    setMonth(m);
    setForm(last ? Object.fromEntries(fields.map((f) => [f.key, last[f.key]])) : empty);
  }, [open, rows, fields, empty]);

  const pick = (m) => {
    setMonth(m);
    const row = rows.find((r) => r.month === m);
    setForm(row ? Object.fromEntries(fields.map((f) => [f.key, row[f.key]])) : empty);
  };

  const save = async () => {
    if (!/^\d{4}-\d{2}$/.test(month)) { toast.error("Month must be YYYY-MM"); return; }
    setBusy(true);
    try {
      const payload = { month, ...Object.fromEntries(fields.map((f) => [f.key, Number(form[f.key]) || 0])) };
      await api.put(`/kpis/${month}`, payload);
      toast.success(`Saved ${fmtMonth(month)}`);
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:w-screen max-sm:max-w-none max-sm:rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Edit KPI data</DialogTitle>
          <DialogDescription>Pick an existing month or type a new one (YYYY-MM). Turnover rate is computed automatically.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Existing month</Label>
            <Select value={rows.some((r) => r.month === month) ? month : ""} onValueChange={pick}>
              <SelectTrigger data-testid="kpi-month-select" className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{rows.map((r) => <SelectItem key={r.month} value={r.month}>{fmtMonth(r.month)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Or new month (YYYY-MM)</Label>
            <Input data-testid="kpi-month-input" value={month} onChange={(e) => pick(e.target.value)} placeholder="2026-07" className="mt-1" />
          </div>
          {fields.map((f) => (
            <div key={f.key}>
              <Label>{f.label}{f.unit && <span className="text-slate-400 ml-1">({f.unit})</span>}</Label>
              <Input data-testid={`kpi-input-${f.key}`} type="number" step="any" value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-1" />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="kpi-save-btn" onClick={save} disabled={busy} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">{busy ? "Saving…" : "Save month"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({ fields: [], rows: [], source: null });
  const [events, setEvents] = useState([]);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(() => api.get("/kpis").then((r) => setData(r.data)), []);
  useEffect(() => { load(); api.get("/compliance/calendar").then((r) => setEvents(r.data)); }, [load]);

  const rows = data.rows.map((r) => ({ ...r, label: fmtMonth(r.month) }));
  const last = rows[rows.length - 1] || {};
  const prev = rows[rows.length - 2] || {};
  const d = (k, digits = 0) => (last[k] != null && prev[k] != null) ? Number((last[k] - prev[k]).toFixed(digits)) : null;

  const downloadCsv = () => {
    const cols = ["month", ...data.fields.map((f) => f.key), "turnover_rate", "annualized_turnover"];
    const csv = [cols.join(","), ...data.rows.map((r) => cols.map((c) => r[c] ?? "").join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `lumis-hr-kpis-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
    toast.success("KPI CSV downloaded");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 print-area">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">Workforce Analytics</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2A5E]">HR KPI Dashboard</h1>
          <p className="text-sm text-slate-600 mt-1">
            {rows.length ? `${rows.length} months · latest ${last.label}` : "No data yet"} ·{" "}
            <Link to="/app/practices?g=bp-hr-kpis" data-testid="kpi-source-link" className="text-[#0A2A5E] hover:underline">Definitions & formulas: {data.source?.citation}</Link>
          </p>
        </div>
        <div className="no-print flex flex-wrap gap-2 w-full sm:w-auto">
          <Button data-testid="print-dashboard-btn" variant="outline" onClick={() => window.print()} className="border-slate-300 flex-1 sm:flex-none"><Printer className="w-4 h-4 mr-2" /> Print / Save PDF</Button>
          <Button data-testid="download-kpis-btn" variant="outline" onClick={downloadCsv} className="border-[#F5A623] text-[#7C3F00] hover:bg-[#FDECD0] flex-1 sm:flex-none"><Download className="w-4 h-4 mr-2" /> Download CSV</Button>
          {user?.role === "manager" && (
            <Button data-testid="edit-kpis-btn" onClick={() => setEditOpen(true)} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white w-full sm:w-auto"><Pencil className="w-4 h-4 mr-2" /> Edit KPI data</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-6">
        <KpiCard tid="kpi-headcount" icon={Users} label="Headcount" value={last.headcount ?? "—"} delta={d("headcount")} good="up" />
        <KpiCard tid="kpi-turnover" icon={TrendingDown} label="Monthly turnover" value={last.turnover_rate ?? "—"} unit="%" delta={d("turnover_rate", 2)} good="down" />
        <KpiCard tid="kpi-tth" icon={Clock} label="Time to hire" value={last.time_to_hire_days ?? "—"} unit="days" delta={d("time_to_hire_days")} good="down" />
        <KpiCard tid="kpi-offer" icon={Percent} label="Offer acceptance" value={last.offer_acceptance_rate ?? "—"} unit="%" delta={d("offer_acceptance_rate")} good="up" />
        <KpiCard tid="kpi-engagement" icon={HeartHandshake} label="Engagement" value={last.engagement_score ?? "—"} unit="%" delta={d("engagement_score")} good="up" />
        <KpiCard tid="kpi-cph" icon={DollarSign} label="Cost per hire" value={last.cost_per_hire?.toLocaleString() ?? "—"} unit="$" delta={d("cost_per_hire")} good="down" />
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4 print-break">
          <ChartCard tid="chart-headcount" title="Headcount, hires & separations" subtitle="Monthly movement">
            <ResponsiveContainer>
              <ComposedChart data={rows} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={AXIS_TICK} /><YAxis yAxisId="l" tick={AXIS_TICK} /><YAxis yAxisId="r" orientation="right" tick={AXIS_TICK} domain={["dataMin - 10", "dataMax + 10"]} />
                <Tooltip /><Legend wrapperStyle={LEGEND_STYLE} />
                <Bar yAxisId="l" dataKey="hires" name="Hires" fill={GREEN} radius={[3, 3, 0, 0]} />
                <Bar yAxisId="l" dataKey="separations" name="Separations" fill={ROSE} radius={[3, 3, 0, 0]} />
                <Line yAxisId="r" dataKey="headcount" name="Headcount" stroke={NAVY} strokeWidth={2.5} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard tid="chart-turnover" title="Turnover rate" subtitle="Separations ÷ headcount · target ≤ 1.5%/mo">
            <ResponsiveContainer>
              <AreaChart data={rows} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={AXIS_TICK} /><YAxis tick={AXIS_TICK} unit="%" />
                <Tooltip formatter={(v) => `${v}%`} /><ReferenceLine y={1.5} stroke={ORANGE} strokeDasharray="4 4" label={{ value: "target", fontSize: 10, fill: "#C7761A" }} />
                <Area dataKey="turnover_rate" name="Turnover %" stroke={BLUE} fill="#DBEAFE" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard tid="chart-hiring" title="Time to hire & offer acceptance" subtitle="Recruiting efficiency">
            <ResponsiveContainer>
              <ComposedChart data={rows} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={AXIS_TICK} /><YAxis yAxisId="l" tick={AXIS_TICK} unit="d" /><YAxis yAxisId="r" orientation="right" tick={AXIS_TICK} unit="%" domain={[60, 100]} />
                <Tooltip /><Legend wrapperStyle={LEGEND_STYLE} />
                <Bar yAxisId="l" dataKey="time_to_hire_days" name="Time to hire (days)" fill={NAVY} radius={[3, 3, 0, 0]} />
                <Line yAxisId="r" dataKey="offer_acceptance_rate" name="Offer acceptance %" stroke={ORANGE} strokeWidth={2.5} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard tid="chart-people" title="Engagement, training & absenteeism" subtitle="Culture & development indicators">
            <ResponsiveContainer>
              <ComposedChart data={rows} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={AXIS_TICK} /><YAxis yAxisId="l" tick={AXIS_TICK} /><YAxis yAxisId="r" orientation="right" tick={AXIS_TICK} unit="%" domain={[50, 100]} />
                <Tooltip /><Legend wrapperStyle={LEGEND_STYLE} />
                <Bar yAxisId="l" dataKey="training_hours_per_employee" name="Training hrs/employee" fill="#93C5FD" radius={[3, 3, 0, 0]} />
                <Line yAxisId="l" dataKey="absenteeism_rate" name="Absenteeism %" stroke={ROSE} strokeWidth={2} />
                <Line yAxisId="r" dataKey="engagement_score" name="Engagement %" stroke={NAVY} strokeWidth={2.5} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <aside className="lg:col-span-4">
          <Card data-testid="compliance-calendar" className="p-5 bg-white border-slate-200">
            <div className="flex items-center gap-2 mb-1"><CalendarDays className="w-4 h-4 text-[#C7761A]" /><h3 className="font-display font-bold text-[#0A2A5E]">Compliance calendar</h3></div>
            <p className="text-xs text-slate-500 mb-4">Federal reporting & posting windows, sorted by urgency.</p>
            <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
              {events.map((e) => (
                <div key={e.id} data-testid={`compliance-${e.id}`} className="p-3 rounded-lg border border-slate-200 hover:border-[#F5A623] transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold text-[#0A2A5E] leading-snug">{e.title}</div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase whitespace-nowrap ${e.status === "open" ? "bg-emerald-100 text-emerald-700" : e.days_until <= 30 ? "bg-[#FDECD0] text-[#7C3F00]" : "bg-slate-100 text-slate-600"}`}>
                      {e.status === "open" ? `Open · ${e.days_left}d left` : `In ${e.days_until}d`}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{e.agency} · {e.start === e.end ? e.start : `${e.start} → ${e.end}`}</div>
                  <div data-testid={`compliance-applies-${e.id}`} className={`text-[11px] mt-1 font-semibold ${/federal-sector/i.test(e.applies_to) ? "text-[#7C3F00]" : "text-slate-600"}`}>Applies to: {e.applies_to}</div>
                  <p className="text-xs text-slate-600 mt-1.5">{e.description}</p>
                  <a href={e.source_url} target="_blank" rel="noreferrer" className="text-[11px] text-[#0A2A5E] hover:underline inline-flex items-center gap-1 mt-1.5">Source <ExternalLink className="w-3 h-3" /></a>
                </div>
              ))}
            </div>
            {user?.role === "manager" && <div className="no-print"><ReminderSettings /></div>}
          </Card>
          {user?.role === "manager" && <div className="no-print"><DigestPanel /></div>}
        </aside>
      </div>

      <KpiEditor open={editOpen} onOpenChange={setEditOpen} fields={data.fields} rows={data.rows} onSaved={load} />
    </div>
  );
}
